import * as React from 'react';
import debounce from 'lodash.debounce';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from "@mui/material/TextField";
import Skeleton from '@mui/material/Skeleton';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from '@mui/icons-material/Search';
import { Grid, InputAdornment, Stack } from '@mui/material';
import SearchListGrid from '../searchListGrid/SearchListGrid';
import {
    IOverride,
    IOverrideSearchParam
} from '../../../store/roleOverRide/types';
import { HeadCell, Order } from '../tableGrid/types';
import EnhancedTable from '../tableGrid/tableGrid';
import EnhancedTableToolbar from '../tableGrid/tableToolbar';
import EnhancedTableHead from '../tableGrid/tableHead';
import Row from '../tableGrid/tableRow';
import { useNavigate } from 'react-router-dom';
import {
    deleteOverrideReq,
    fetchOverridesRequest,
} from "../../../store/roleOverRide/actions"
import { getOverrideListPendingSelector, getOverrideListSelector, deleteOverrideSelector, deleteOverridePendingSelector } from '../../../store/roleOverRide/selectors';
import { Button } from '@mui/material';
import CustomizedDialogs from '../../atoms/modal/customizedDialogs';
import { convertUTCDateToLocalDate } from '../../shared/functions';
import { SEARCH_DEBOUNCE_TIME_MILLIS } from "../../../constants/common";
import {getProfileDataSelector, getRoleClaimsSelector} from "../../../store/auth/selectors";
import Box from '@mui/material/Box';
import {OverridableStringUnion} from "@mui/types";
import { postOverrideRequest } from "../../../store/requestOverride/actions";


// settings data

const headCells: HeadCell[] = [
    {
        id: 'expired',
        numeric: false,
        disablePadding: false,
        label: '',
        width: "5px",
        disableSort: true
    },
    {
        id: 'sourceId',
        numeric: false,
        disablePadding: false,
        label: 'Source',
        width: "130px",
    },
    {
        id: 'tableId',
        numeric: false,
        disablePadding: false,
        label: 'Dataset Name',
        width: "200px",
    },
    {
        id: 'grantee',
        numeric: false,
        disablePadding: false,
        label: 'Grantee',
        width: "200px",
    },
    {
        id: 'treatmentLevel',
        numeric: false,
        disablePadding: false,
        label: 'Treatment',
        width: "5px",
    },
    {
        id: 'expiryDate',
        numeric: false,
        disablePadding: false,
        label: 'Expiry Date',
        width: "250px",
    },
    {
        id: 'businessReason',
        numeric: false,
        disablePadding: false,
        label: '',
        width: "5px",
    }
];

const myOverridesHeadCells: HeadCell[] = [
    {
        id: 'expired',
        numeric: false,
        disablePadding: false,
        label: '',
        width: "5px",
        disableSort: true
    },
    {
        id: 'sourceId',
        numeric: false,
        disablePadding: false,
        label: 'Source',
        width: "150px",
    },
    {
        id: 'tableId',
        numeric: false,
        disablePadding: false,
        label: 'Dataset Name',
        width: "200px",
    },
    {
        id: 'treatmentLevel',
        numeric: false,
        disablePadding: false,
        label: 'Treatment',
        width: "5px",
    },
    {
        id: 'expiryDate',
        numeric: false,
        disablePadding: false,
        label: 'Expiry Date',
        width: "200px",
    },
    {
        id: 'businessReason',
        numeric: false,
        disablePadding: false,
        label: '',
        width: "20px",
    }
];

const filterCells: string[] = [
    'sourceId', 'tableId', 'grantee', 'treatmentLevel', 'daysToExpiry'
]

const myOverridesFilterCells: string[] = filterCells.filter(item => item !== 'grantee');

class OverrideExplorerPrams {
    myOverrides?: boolean = false;
    tableId?: string = '';
    grantee?: string = '';
}

const OverRideExplorer = (params: OverrideExplorerPrams) => {


    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [dataRows, setDataRows] = useState<IOverride[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [order, setOrder] = useState<Order>("asc");
    const [orderBy, setOrderBy] = useState<keyof IOverride>("tableId");
    const [selected, setSelected] = useState<readonly string[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [open, setOpen] = useState(false);
    const [openRenew, setOpenRenew] = useState(false);
    const [rowID, setRowID] = useState('');
    const [rowGrantee, setRowGrantee] = useState('');
    const [rowGranteeType, setRowGranteeType] = useState('');
    const [rowTableId, setRowTableId] = useState('');
    const [rowSourceId, setRowSourceId] = useState('');
    const [rowTreatmentLevel, setRowTreatmentLevel] = useState('');
    const [rowBusinessReason, setRowBusinessReason] = useState('');
    const [rowExpiryDate, setRowExpiryDate] = useState('');
    const [deletedIds, setDeletedIds] = useState<string[]>([]);
    const [renewedIds, setRenewedIds] = useState<string[]>([]);
    const deleteOverride = useSelector(deleteOverrideSelector)
    const deletePendingOverride = useSelector(deleteOverridePendingSelector)
    const userProfile = useSelector(getProfileDataSelector);
    const roles = useSelector(getRoleClaimsSelector); // roles  - IDP.Admin or IDP.User

    const [isAdmin, setIsAdmin] = useState(true);
    
    useEffect(() => {
    if (roles && roles.length > 0) {
        setIsAdmin(roles.includes("IDP.Admin") ? true : false);
    }
    }, [roles]);

    
    let grantee = params.grantee ? params.grantee.toLowerCase() : '';
    if (params.myOverrides) {
        grantee = userProfile.userPrincipalName.toLowerCase();
    }

    // Filters-related
    const [tableIds, setTableIds] = useState<string[]>(params.tableId ? [params.tableId] : []);
    const [sourceIds, setSourceIds] = useState<string[]>([]);
    const [daysToExpiry, setDaysToExpiry] = useState<number | null>();
    const [treatmentLevels, setTreatmentLevels] = useState<string[]>([]);
    const [grantees, setGrantees] = useState<string[]>(grantee ? [grantee] : []);
    const [filters, setFilters] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState(false);
    const [deletedIdLoading, setDeletedIdLoading] = useState('')
    const selectable: string = "no";
    const allSelectable: boolean = false;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // source list    
    const overrideList = useSelector(getOverrideListSelector);
    const loadingOverrideList = useSelector(getOverrideListPendingSelector);

    const [searchParams, setSearchParams] = useState<IOverrideSearchParam>({
        skip: page * rowsPerPage,
        top: rowsPerPage,
        count: true,
        search: searchTerm,
        tableId: tableIds,
        sourceId: sourceIds,
        daysToExpiry: daysToExpiry,
        treatmentLevel: treatmentLevels,
        grantee: grantees,
        orderby: `${orderBy} ${order}`,
    });

    useEffect(() => {
        window.scrollTo(0, 0)
        setSearchParams({
            skip: page * rowsPerPage,
            top: rowsPerPage,
            count: true,
            search: searchTerm,
            tableId: tableIds,
            sourceId: sourceIds,
            daysToExpiry: daysToExpiry,
            treatmentLevel: treatmentLevels,
            grantee: grantees,
            orderby: `${orderBy} ${order}`
        })
    }, [page, rowsPerPage, searchTerm, orderBy, order, grantees, tableIds, sourceIds, treatmentLevels, daysToExpiry]);
    
    useEffect(() => {
        dispatch(fetchOverridesRequest(searchParams));
    }, [dispatch, searchParams]);

    function expiredStatus(expiryDate: any): OverridableStringUnion<'error' | 'warning' | 'success'> {
      let currentDate = new Date();
      let expiry = new Date(expiryDate);
      let nowPlus30Days = new Date();
      nowPlus30Days.setDate(nowPlus30Days.getDate() + 30);
      if (expiry < currentDate) {
          return "error";
      } else if (expiry > currentDate && expiry < nowPlus30Days) {
          return "warning";
      } else {
          return "success";
      }
    }

    useEffect(() => {
        let data: IOverride[] = (overrideList.overrides);
        if (data && data.length > 0) {
            const newData = data.map((item: any, index: number) => {
                return {
                    ...item,
                    expiryDateUTC: item.expiryDate,
                    expiryDate: convertUTCDateToLocalDate(item.expiryDate).toString(),
                    expired: expiredStatus(item.expiryDate),
                    index
                };
            })
            setDataRows(newData);
        }
        else {
            setDataRows(data);
        }


        setTotalRows(overrideList.count);
    }, [overrideList]);

    // table related events 

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property as keyof IOverride);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = dataRows!.map((n: { tableId: any; }) => n.tableId);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const deleteAllClick = () => {
        setSelected([]);
    }

    const filterResults = (event: React.MouseEvent<HTMLButtonElement>, filters: string[]) => {

        let newTables: string[] = params.tableId ? [params.tableId] : [];
        let newSources: string[] = [];
        let newTreatmentLevels: string[] = [];
        let newGrantees: string[] = grantee ? [grantee] : [];
        let newDaysToExpiry: number | null = null;


        filters.forEach(item => {
            let filter: string[] = item.split(": ");
            switch (filter[0]) {
                case 'tableId':
                    newTables.push(filter[1]);
                    break;
                case 'sourceId':
                    newSources.push(filter[1]);
                    break;
                case 'treatmentLevel':
                    newTreatmentLevels.push(filter[1]);
                    break;
                case 'grantee':
                    newGrantees.push(filter[1].toLowerCase());
                    break;
                case 'daysToExpiry':
                    let filterDaysToExpiry = parseInt(filter[1])
                    if (!newDaysToExpiry || filterDaysToExpiry > newDaysToExpiry) {
                        newDaysToExpiry = filterDaysToExpiry;
                    }
                    break;
                default:
                    break;
            }
        });

        setDaysToExpiry(newDaysToExpiry);
        setTableIds(newTables);
        setSourceIds(newSources);
        setTreatmentLevels(newTreatmentLevels);
        setGrantees(newGrantees);
        setFilters(filters);
        setOpenFilters(true);
        setPage(0);
    }

    const refreshPage = () => {
        setTableIds(params.tableId ? [params.tableId] : []);
        setSourceIds([]);
        setDaysToExpiry(0)
        setTreatmentLevels([]);
        setGrantees(grantee ? [grantee] : []);
        setFilters([]);
        setOpenFilters(false);
    }

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPage(0);
        setSearchTerm(e.target.value);
    }

    const handleSearchDebounce = useMemo(() => {
        return debounce(handleSearch, SEARCH_DEBOUNCE_TIME_MILLIS);

    }, []);

    const searchEl = () => {
        return (
            <TextField
                id="override-search"
                label="Search Overrides"
                type="search"
                fullWidth={true}
                onChange={handleSearchDebounce}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                }}
            />
        );
    };

    const handleOverrideView = (row: IOverride, event: React.MouseEvent<HTMLButtonElement>) => {
        const url = `/ViewOverride/${row.overrideId}`;
        if (event.ctrlKey) {
            window.open(url, '_blank')
        }
        else {
            navigate(url);
        }
    }

    const handleOverrideEdit = (row: IOverride, event: React.MouseEvent<HTMLButtonElement>) => {
        const url = `/EditOverride/${row.overrideId}`;
        if (event.ctrlKey) {
            window.open(url, '_blank')
        }
        else {
            navigate(url);
        }
    }

    const confirmOverrideDelete = (row: IOverride) => {
        setOpen(true);
        setRowID(row.overrideId);
        setRowGrantee(row.grantee);
        setRowTableId(row.tableId);
    }

    const handleOverrideDelete = () => {
        setOpen(false);
        dispatch(deleteOverrideReq(rowID));
        setDeletedIdLoading(rowID)
        setDeletedIds(x => [...x, rowID]);
    }

    const handleClose = () => {
        setOpen(false);
    }

    const confirmOverrideRenew = (row: IOverride) => {
        setRowID(row.overrideId);
        setRowGrantee(row.grantee);
        setRowTableId(row.tableId);
        setRowSourceId(row.sourceId);
        setRowGranteeType(row.granteeType);
        setRowTreatmentLevel(row.treatmentLevel);
        setRowBusinessReason(row.businessReason);
        setRowExpiryDate(row.expiryDateUTC);
        setOpenRenew(true);
    }

    const handleOverrideRenew = () => {
        setOpenRenew(false);
        let payload = [
            {
                sourceId: rowSourceId,
                tableId: rowTableId,
                grantee: rowGrantee,
                granteeType: rowGranteeType,
                treatmentLevel: rowTreatmentLevel,
                businessReason: rowBusinessReason,
                overrideId: rowID,
                expiryDate: rowExpiryDate.split('T')[0]
            }
        ];
        dispatch(postOverrideRequest(payload));
        setRenewedIds(x => [...x, rowID]);
    }
    
    const handleCloseRenew = () => {
        setOpenRenew(false);
    }

    const tableToolBar = (props: OverrideExplorerPrams) => {
        if (props.tableId) {
            return <></>
        }
        let filterDropdown = props.myOverrides ? myOverridesFilterCells : filterCells;
        return (
            <EnhancedTableToolbar numSelected={selected.length} title={'Overrides'} headCells={headCells} filterCells={filterDropdown} deleteAll={deleteAllClick}
                filterResults={filterResults} refreshPage={refreshPage} currentFilters={filters} openToolbar={openFilters} explorer={'overrides'}/>
        );
    }

    const tableHeader = (props: OverrideExplorerPrams) => {
        return (
            <EnhancedTableHead
                headCells={props.myOverrides ? myOverridesHeadCells : headCells}
                numSelected={selected.length}
                actionable={true}
                selectable={allSelectable}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={totalRows}
            />
        );
    }

    const isSelected = (name: string) => selected.indexOf(name) !== -1;

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalRows) : 0;

    const tableRows = (props: OverrideExplorerPrams) => {

        return (
            <>
                {dataRows?.map((row: IOverride) => {
                        const isItemSelected = isSelected(row.overrideId);
                        const expired = expiredStatus(row.expiryDate);
                        const canRenewOverride = expired === "error" || expired === "warning";
                        const renewRequested = renewedIds.includes(row.overrideId);
                        return (
                            <Row row={row}
                                key={row.overrideId}
                                headCells={props.myOverrides ? myOverridesHeadCells : headCells}
                                selectable={selectable}
                                canEdit={!props.myOverrides && isAdmin}
                                canView={true}
                                canDelete={!props.myOverrides && isAdmin}
                                canExpand={false}
                                canRenew={canRenewOverride && isAdmin}
                                isItemSelected={isItemSelected}
                                handleView={handleOverrideView}
                                handleEdit={handleOverrideEdit}
                                handleDelete={confirmOverrideDelete}
                                handleRenew={confirmOverrideRenew}
                                deletedRows={deletedIds}
                                loadingIcon={deletePendingOverride}
                                deletedIdLoading={deletedIdLoading}
                                deleteId={deleteOverride}
                                disabledButton={renewRequested}
                            >
                            </Row>
                        );
                    })}
                {emptyRows > 0 && emptyRows < 10 && (
                    <TableRow
                        style={{
                            height: (53) * emptyRows,
                        }}
                    >
                        <TableCell colSpan={12} />
                    </TableRow>
                )}
                {dataRows?.length < 1 && (
                    <TableRow
                        style={{
                            height: (53) * emptyRows,
                        }}
                    >
                        <TableCell colSpan={6}>There are no overrides available.</TableCell>
                    </TableRow>
                )}
            </>
        );
    }

    const listGrid = (props: OverrideExplorerPrams) => {

        return (
            <>
                {/* adding skeleton screen on loading */}
                {
                    loadingOverrideList ?
                        <Grid container width="100%">
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                        </Grid>

                        :
                        <EnhancedTable
                            totalRows={totalRows}
                            tableToolBar={tableToolBar(props)}
                            tableHeader={tableHeader(props)}
                            tableRows={tableRows(props)}
                            pagination={true}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                        />
                }
                <CustomizedDialogs
                    dialogHeight=''
                    modalSize="md"
                    key="target"
                    modalTitle="Confirm Override Extension Request"
                    isOpen={openRenew}
                    onClose={handleCloseRenew}
                    footerAction={
                        <Stack direction="row" spacing={2}>
                            <Button id="699da8ed-391b-45bd-b885-05e5204a7e53" color="primary"
                                    onClick={handleOverrideRenew} autoFocus>
                                Yes
                            </Button>
                            <Button id="8b516b49-0311-4677-a78f-82639875b6d5" color="primary" onClick={handleCloseRenew}>
                                No
                            </Button>
                        </Stack>
                    }
                >
                    <> Do you want to renew {rowTreatmentLevel} override for {rowTableId} with grantee {rowGrantee}?</>
                    <br />
                    <br />
                    Business reason: 
                    <br />
                    <textarea 
                        style={{height: '64px', fontSize: '16px', width: '100%'}}
                        value={rowBusinessReason} 
                        onChange={(e) => setRowBusinessReason(e.target.value)} 
                    />
                </CustomizedDialogs>
            </>
        );
    }


    if (params.myOverrides) {
        return (
            <>
                <SearchListGrid searchEl={searchEl()}  hideSearch={!!params.tableId} children={
                    <Box sx={{ padding: "10px 0", }}>
                        {listGrid(params)}
                    </Box>
                }/>
            </>
        )
    } else {
        return (
            <>
                <SearchListGrid searchEl={searchEl()} hideSearch={!!params.tableId} children={listGrid(params)}/>
                <CustomizedDialogs
                    dialogHeight=''
                    modalSize="md"
                    key="target"
                    modalTitle="Delete Confirmation"
                    isOpen={open}
                    onClose={handleClose}
                    footerAction={
                        <Stack direction="row" spacing={2}>
                            <Button id="699da8ed-391b-45bd-b885-05e5204a7e53" color="primary"
                                    onClick={handleOverrideDelete} autoFocus>
                                Yes
                            </Button>
                            <Button id="8b516b49-0311-4677-a78f-82639875b6d5" color="primary" onClick={handleClose}>
                                No
                            </Button>
                        </Stack>
                    }
                >
                    <> Are you sure you want to delete the override for {rowTableId} with grantee {rowGrantee}?</>
                </CustomizedDialogs>
            </>
        );
    }
}

export default OverRideExplorer;